import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { useLocation } from "react-router-dom";
import LumientLogoMark from "@/components/LumientLogoMark";
import { NavLink } from "@/components/NavLink";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const DARK_ROUTES = ["/pricing"];

const navItems = [
  { label: "Features", href: "/features" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Pricing", href: "/pricing" },
  { label: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isDark = DARK_ROUTES.includes(pathname);

  useEffect(() => {
    const handler = () => {
      setScrolled(window.scrollY > 24);
    };

    window.addEventListener("scroll", handler, { passive: true });
    handler();

    return () => {
      window.removeEventListener("scroll", handler);
    };
  }, []);

  const linkClass = isDark
    ? "text-sm font-medium transition-colors duration-200 text-white/50 hover:text-white"
    : "text-sm font-medium transition-colors duration-200 text-[#0B0B0B]/65 hover:text-[#0B0B0B]";
  const activeLinkClass = isDark ? "text-white" : "text-[#0B0B0B]";
  const mobileLinkClass =
    "block rounded-lg px-3 py-3 text-base font-medium text-[#0B0B0B]/70 transition-colors hover:bg-muted hover:text-[#0B0B0B]";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 px-6 lg:px-16 py-4 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md border-b" : "bg-transparent border-b border-transparent"
      }`}
      style={
        scrolled
          ? isDark
            ? {
                backgroundColor: "rgba(11,11,11,0.92)",
                borderColor: "rgba(255,255,255,0.08)",
                boxShadow: "0 1px 16px rgba(0,0,0,0.3)",
              }
            : {
                backgroundColor: "oklch(var(--surface-panel) / 0.92)",
                borderColor: "oklch(var(--border))",
                boxShadow: "0 1px 16px oklch(0.12 0.008 75 / 0.05)",
              }
          : undefined
      }
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <a href="/" className="flex items-center gap-3">
          <LumientLogoMark className={`w-8 h-8 ${isDark ? "text-primary" : "text-primary"}`} />
          <span className={`text-sm font-semibold tracking-wide ${isDark ? "text-white" : "text-[#0B0B0B]"}`}>
            Lumient
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <NavLink key={item.href} to={item.href} className={linkClass} activeClassName={activeLinkClass}>
              {item.label}
            </NavLink>
          ))}

          <a
            href="/contact"
            className="glow-button px-5 py-2 text-sm font-semibold rounded-full"
            style={{ letterSpacing: "0.01em" }}
          >
            Book a Demo
          </a>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <a
            href="/contact"
            className="glow-button px-4 py-2 text-xs font-semibold rounded-full"
            style={{ letterSpacing: "0.01em" }}
          >
            Book a Demo
          </a>

          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                  isDark
                    ? "border-white/15 text-white hover:border-white/30"
                    : "border-[#0B0B0B]/10 text-[#0B0B0B] hover:border-[#0B0B0B]/25"
                }`}
                aria-label="Open navigation menu"
              >
                <Menu className="h-4 w-4" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[18rem] border-l border-border bg-background px-5 py-8">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">
                Site navigation links for Lumient pages and demo booking.
              </SheetDescription>
              <div className="mt-8 flex flex-col gap-1">
                {navItems.map((item) => (
                  <SheetClose key={item.href} asChild>
                    <NavLink to={item.href} className={mobileLinkClass} activeClassName="bg-muted text-[#0B0B0B]">
                      {item.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </div>
              <SheetClose asChild>
                <a
                  href="/contact"
                  className="glow-button mt-6 block rounded-full px-5 py-3 text-center text-sm font-semibold"
                >
                  Book a Demo
                </a>
              </SheetClose>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
